import React, { useState } from 'react';
import { Mic, MicOff, Loader2, Square, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import logger from '@/lib/logger';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';

function formatDuration(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

/**
 * VoiceNoteButton — records audio, transcribes via Whisper, then runs the
 * transcript through Claude for a summary + action items.
 *
 * Props:
 *   - online: bool
 *   - context: string — optional grounding (e.g. "Lead: John at Acme")
 *   - onResult: ({transcript, summary, action_items}) => void
 */
export function VoiceNoteButton({ online, context, onResult }) {
  const { state, durationSec, start, stop, cancel } = useVoiceRecorder();
  const [phase, setPhase] = useState('idle'); // idle | transcribing | analyzing

  const handleStart = async () => {
    if (!online) {
      toast.info('Voice notes require an internet connection — type your note or try again later.');
      return;
    }
    await start();
  };

  const handleStop = async () => {
    const blob = await stop();
    if (!blob || blob.size < 1000) {
      toast.info('Recording too short');
      cancel();
      return;
    }
    setPhase('transcribing');
    try {
      const { data: stt } = await api.transcribeVoice(blob);
      const transcript = (stt.text || '').trim();
      if (!transcript) {
        toast.info("Couldn't hear anything — try again");
        setPhase('idle');
        cancel();
        return;
      }
      setPhase('analyzing');
      let summary = '';
      let actionItems = [];
      try {
        const { data: analysis } = await api.analyzeTranscript(transcript, context || null);
        summary = analysis.summary || '';
        actionItems = analysis.action_items || [];
      } catch (e) {
        logger.warn('analyze failed, continuing with transcript only', e);
      }
      onResult({ transcript, summary, action_items: actionItems });
      if (actionItems.length > 0) {
        toast.success(`Found ${actionItems.length} action item${actionItems.length === 1 ? '' : 's'}`);
      } else {
        toast.success('Transcribed');
      }
    } catch (e) {
      logger.error('transcribe failed', e);
      toast.error(e.response?.data?.detail || 'Transcription failed');
    }
    setPhase('idle');
    cancel();
  };

  const isRecording = state === 'recording';
  const isBusy = phase !== 'idle' || state === 'processing';

  let buttonClass = 'bg-white border-slate-200 text-slate-500 hover:border-[#0047AB] hover:text-[#0047AB]';
  let Icon = Mic;
  let title = 'Record a voice note';

  if (!online) {
    buttonClass = 'bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed';
    Icon = MicOff;
    title = 'Voice notes require an internet connection';
  } else if (isRecording) {
    buttonClass = 'bg-red-500 border-red-500 text-white hover:bg-red-600 animate-pulse';
    Icon = Square;
    title = 'Stop recording';
  } else if (phase === 'analyzing') {
    buttonClass = 'bg-violet-50 border-violet-200 text-violet-600';
    Icon = Sparkles;
    title = 'Extracting action items…';
  } else if (isBusy) {
    buttonClass = 'bg-white border-slate-200 text-slate-400';
    Icon = Loader2;
    title = 'Transcribing…';
  }

  let label;
  if (isRecording) label = `Recording ${formatDuration(durationSec)}`;
  else if (phase === 'transcribing') label = 'Transcribing…';
  else if (phase === 'analyzing') label = 'Analyzing…';
  else label = 'Voice Note';

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={isRecording ? handleStop : handleStart}
        disabled={(isBusy && !isRecording) || !online}
        className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-sm border text-xs font-medium transition-all ${buttonClass}`}
        title={title}
        data-testid="voice-note-btn"
      >
        <Icon className={`w-3.5 h-3.5 ${phase === 'transcribing' ? 'animate-spin' : ''} ${phase === 'analyzing' ? 'animate-pulse' : ''}`} />
        <span className="hidden sm:inline">{label}</span>
      </button>
      {isRecording && (
        <button
          type="button"
          onClick={cancel}
          className="text-[10px] uppercase tracking-wide text-slate-400 hover:text-slate-700"
          data-testid="voice-note-cancel"
        >
          Cancel
        </button>
      )}
    </div>
  );
}
